import { useTranslation } from 'react-i18next';

interface PartnerData {
  name: string;
  percentage: number;
  income: number;
  share: number;
  color: string;
  contributions?: number;
  cumulativeBalance?: number;
}

interface PartnerDetailsProps {
  title: string;
  partners: PartnerData[];
  formatCurrency: (amount: number) => string;
  noCard?: boolean;
  cumulativeStartDate?: string | null;
}

export default function PartnerDetails({
  title,
  partners,
  formatCurrency,
  noCard,
  cumulativeStartDate,
}: PartnerDetailsProps) {
  const { t, i18n } = useTranslation('dashboard');

  const formatDateLocale = (dateStr: string): string => {
    const d = new Date(dateStr);
    return d.toLocaleDateString(i18n.language, { day: 'numeric', month: 'long', year: 'numeric' });
  };

  const content = (
    <>
      <h2 className="text-lg font-semibold text-gray-900 mb-4">{title}</h2>
      <div className="space-y-6">
        {partners.map((partner, index) => (
          <div key={index}>
            <div className="flex items-center justify-between mb-2">
              <span className="font-medium text-gray-900">{partner.name}</span>
              <span className="font-semibold" style={{ color: partner.color }}>
                {partner.percentage.toFixed(1)}%
              </span>
            </div>
            <div className="space-y-1 text-sm text-gray-600">
              <div className="flex justify-between">
                <span>{t('partnerDetailsLabels.income')}</span>
                <span>{formatCurrency(partner.income)}</span>
              </div>
              <div className="flex justify-between">
                <span>{t('partnerDetailsLabels.expenseShare')}</span>
                <span>{formatCurrency(partner.share)}</span>
              </div>
              {partner.contributions !== undefined && (
                <div className="flex justify-between">
                  <span>{t('partnerDetailsLabels.contributions')}</span>
                  <span>{formatCurrency(partner.contributions)}</span>
                </div>
              )}
              {partner.cumulativeBalance !== undefined && (
                <div className="flex justify-between font-medium">
                  <span>{cumulativeStartDate ? t('partnerDetailsLabels.cumulativeBalanceSince', { date: formatDateLocale(cumulativeStartDate) }) : t('partnerDetailsLabels.cumulativeBalance')}</span>
                  <span className={partner.cumulativeBalance >= 0 ? 'text-green-600' : 'text-red-600'}>
                    {partner.cumulativeBalance >= 0 ? '+' : ''}{formatCurrency(partner.cumulativeBalance)}
                  </span>
                </div>
              )}
            </div>
            <div className="mt-2 h-2 bg-gray-200 rounded-full overflow-hidden">
              <div
                className="h-full rounded-full"
                style={{ width: `${partner.percentage}%`, backgroundColor: partner.color }}
              />
            </div>
          </div>
        ))}
      </div>
    </>
  );

  if (noCard) return content;
  return <div className="card">{content}</div>;
}
